import { assert, Test } from "oc-e2e-testrunner";
import { sleep } from "oc-e2e-testrunner/utils";
import {
  assertNumberOfAufbauten,
  assertNumberOfElements,
  clickSleepAndGetCurrentElement,
  resetArrangement,
  sleepUntilReady,
} from "./TestTorkonfiguratorFunctions";
import { assertEqualsBaugruppen, assertEqualsBestellliste } from "../../testsE2E/utils";

export default {
  name: "TestTorkonfigurator",
};

/**
 * Wählt nacheinander die Workflows an und prüft ob der Workflow gewechselt wurde, das richtige
 * Element da steht und die richtige Anzahl Elemente auf der Strecke ist
 */
export const testChangeWorkflow: Test = async () => {
  resetArrangement();
  await sleepUntilReady();

  const konf = APP.arrangement.konfiguration;

  let currentElement = await clickSleepAndGetCurrentElement("WORKFLOW_Doppeltor");
  assert.isTrue(currentElement.isDoppeltor, "Element ist kein Doppeltor");
  assert.isFalse(currentElement.isEAntrieb, "Doppeltor ist E-Antrieb (Soll: normales Doppeltor)");
  assert.equals(
    konf.getAuswahlwert("Workflow").Key,
    "WORKFLOW_Doppeltor",
    "Workflow ist nicht Doppeltor"
  );
  assertNumberOfElements(1);

  currentElement = await clickSleepAndGetCurrentElement("WORKFLOW_Zaunelement");
  assert.isFalse(currentElement.isTor, "Element ist Tor, obwohl Zaunelement gewünscht");
  assert.equals(
    konf.getAuswahlwert("Workflow").Key,
    "WORKFLOW_Zaunelement",
    "Workflow ist nicht Zaunelement"
  );
  assertNumberOfElements(1);

  currentElement = await clickSleepAndGetCurrentElement("WORKFLOW_Doppeltor_EAntrieb");
  await sleep(200);
  assert.isTrue(currentElement.isDoppeltor, "Element ist kein Doppeltor");
  assert.isTrue(currentElement.isEAntrieb, "Doppeltor ist kein E-Antrieb");
  assert.equals(
    konf.getAuswahlwert("Workflow").Key,
    "WORKFLOW_Doppeltor_EAntrieb",
    "Workflow ist nicht Doppeltor E-Antrieb"
  );
  assertNumberOfElements(1);

  currentElement = await clickSleepAndGetCurrentElement("WORKFLOW_Dreifachtor");
  assert.isTrue(currentElement.isDreifachtor, "Element ist kein Dreifachtor");
  assert.isFalse(
    currentElement.isEAntrieb,
    "Dreifachtor mit E-Antrieb??? (Soll: normales Dreifachtor)"
  );
  assert.equals(
    konf.getAuswahlwert("Workflow").Key,
    "WORKFLOW_Dreifachtor",
    "Workflow ist nicht Dreifachtor"
  );
  assertNumberOfElements(2);

  currentElement = await clickSleepAndGetCurrentElement("WORKFLOW_Einzeltor");
  assert.isTrue(currentElement.isEinzeltor, "Element ist kein Einzeltor");
  assert.isFalse(
    currentElement.isEAntrieb,
    "Einzeltor mit E-Antrieb??? (Soll: normales Einzeltor)"
  );
  assert.equals(
    konf.getAuswahlwert("Workflow").Key,
    "WORKFLOW_Einzeltor",
    "Workflow ist nicht Einzeltor"
  );
  assertNumberOfElements(1);
};

/**
 * Wählt nacheinander die Mauer-Optionen an und schaut ob die Szene ungefähr stimmt
 */
export const testChangeMauer: Test = async () => {
  resetArrangement();
  await sleepUntilReady();

  let currentElement = await clickSleepAndGetCurrentElement("AnMauer_links");
  assert.isTrue(currentElement.isEinzeltor, "Element ist kein Einzeltor");
  assertNumberOfElements(1);
  assertNumberOfAufbauten(1);

  currentElement = await clickSleepAndGetCurrentElement(
    "AnMauer_beide",
    () => document.querySelector(".lichteWeiteInputWrapper") != null
  );
  const gewünschteLichteWeite = 90;
  (document.querySelector(".lichteWeiteInputWrapper input") as HTMLInputElement).value =
    gewünschteLichteWeite as unknown as string;
  (document.querySelector(`.primary-button`) as HTMLButtonElement).click();
  await sleepUntilReady();

  assert.isTrue(currentElement.isEinzeltor, "Element ist kein Einzeltor");
  assertNumberOfElements(1);
  assertNumberOfAufbauten(2);
  const mauern = APP.arrangement.aufbauten;
  const echteLichteWeite = mauern[1].position.x - mauern[0].position.x - mauern[0].size.x;
  assert.equals(
    gewünschteLichteWeite / 100,
    echteLichteWeite,
    "Mauerlücke entspricht nicht eingegebenem Maß",
    0.001
  );

  currentElement = await clickSleepAndGetCurrentElement("AnMauer_nein");
  assert.isTrue(currentElement.isEinzeltor, "Element ist kein Einzeltor");
  assertNumberOfElements(1);
  assertNumberOfAufbauten(0);
};

/**
 * Schaltet ein paar Menüpunkte durch und schaut ob die wesentlichen Elemente vorhanden sind
 */
export const testChangeMenu: Test = async () => {
  resetArrangement();
  await sleepUntilReady();

  APP.optionMenu.selectItemByKey("Mass");
  await sleep(500);
  assert.isNotNull(
    document.querySelector(".selectedContent .optionSelect.Masse"),
    "Masse Tab hat keine Masse"
  );

  APP.optionMenu.selectItemByKey("Abschluss");
  await sleep(500);
  assert.isNotNull(
    document.querySelector(".selectedContent .PartsList"),
    "Abschluss Tab hat keine Partslist"
  );
  assert.isNotNull(
    document.querySelector("body.bigPreview"),
    "Abschluss-Tab hat nicht body.bigPreview-Klasse"
  );

  APP.optionMenu.selectItemByKey("Fuellung");
  await sleep(500);
  assert.isTrue(
    document.querySelectorAll(".selectedContent .option").length > 1,
    "Füllung Tab hat keine Auswahl"
  );
  assert.isNull(
    document.querySelector("body.bigPreview"),
    "Füllung-Tab hat body.bigPreview-Klasse"
  );
};

/**
 * Erstellt ein Element (Doppeltor Rhombus mit Akzent) und prüft die Stückliste
 */
export const testStückliste: Test = async () => {
  resetArrangement();
  await sleepUntilReady();

  const konf = APP.arrangement.konfiguration;

  await clickSleepAndGetCurrentElement("WORKFLOW_Doppeltor");
  APP.optionMenu.selectItemByKey("Serie");
  await sleep(500);
  await clickSleepAndGetCurrentElement("SYSTEM RHOMBUS");
  APP.optionMenu.selectItemByKey("Füllung");
  await sleep(500);
  await clickSleepAndGetCurrentElement("SYSTEM Rhombus Anthrazit");
  await sleep(100);
  await clickSleepAndGetCurrentElement("Basisprofile_Rhombus_2_Tor");
  await sleep(100);
  (
    document.querySelector(
      `.optionSelect.Profil div[data-auswahlwert-key='SYSTEM Rhombus Silber']`
    ) as HTMLElement
  ).click();
  await sleepUntilReady();

  assertEqualsBestellliste(
    [
      {
        ArtikelID: "2649",
        Anzahl: 1,
      },
      {
        ArtikelID: "2556_rhombus",
        Anzahl: 1,
      },
      {
        ArtikelID: "2018",
        Anzahl: 1,
      },
      {
        ArtikelID: "1251",
        Anzahl: 1,
      },
      {
        ArtikelID: "2550",
        Anzahl: 1,
      },
    ],
    APP.arrangement.bestellliste
  );
  assertEqualsBaugruppen(
    [
      [
        {
          ArtikelID: "2556_rhombus",
          MassOrBauseitsTag: "360 x 180 cm",
          Anzahl: 1,
        },
        {
          ArtikelID: "2018",
          MassOrBauseitsTag: "360 x 180 cm",
          Anzahl: 1,
        },
        {
          ArtikelID: "1251",
          MassOrBauseitsTag: "360 x 180 cm",
          Anzahl: 1,
        },
        {
          ArtikelID: "2649",
          MassOrBauseitsTag: "360 x 180 cm",
          Anzahl: 1,
        },
        {
          ArtikelID: "2550",
          MassOrBauseitsTag: "360 x 180 cm",
          Anzahl: 1,
        },
      ],
    ],
    APP.arrangement.baugruppen
  );
};
